import { SyncStatus } from '@/types';

interface SyncStatusBannerProps {
  syncStatus: SyncStatus;
}

function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';

  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
}

function isStale(dateStr: string | null): boolean {
  if (!dateStr) return true;

  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = diffMs / (1000 * 60);

  // Consider stale if more than 30 minutes old
  return diffMins > 30;
}

export function SyncStatusBanner({ syncStatus }: SyncStatusBannerProps) {
  const { last_synced_at, sync_in_progress, has_error, error } = syncStatus;

  if (sync_in_progress) {
    return (
      <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-4 text-sm text-blue-700">
        <div className="flex items-center">
          <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Syncing data from Salesforce...
        </div>
      </div>
    );
  }

  if (has_error && error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-3 mb-4 text-sm text-red-700">
        <div className="font-medium">Sync Error</div>
        <div className="mt-1">{error}</div>
        <div className="mt-1 text-xs text-red-500">
          Last synced: {formatTimeAgo(last_synced_at)}
        </div>
      </div>
    );
  }

  const stale = isStale(last_synced_at);

  if (stale && last_synced_at) {
    return (
      <div className="bg-amber-50 border-l-4 border-amber-400 p-3 mb-4 text-sm text-amber-700">
        <div className="flex items-center justify-between">
          <span>Data may be out of date. Last synced: {formatTimeAgo(last_synced_at)}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="text-xs text-gray-500 mb-4">
      Last synced: {formatTimeAgo(last_synced_at)}
    </div>
  );
}
